/**
 * Role-based permission utilities
 *
 * Role hierarchy: viewer < agent < admin
 * - admin: Full access (edit, delete, submit to Twilio)
 * - agent: Can review, edit, add notes (no delete)
 * - viewer: Read-only access
 */

export const ROLES = {
  VIEWER: 'viewer',
  AGENT: 'agent',
  ADMIN: 'admin'
};

const ROLE_HIERARCHY = {
  viewer: 1,
  agent: 2,
  admin: 3
};

/**
 * Check if user has at least the required role level
 */
export function hasRole(userRole, requiredRole) {
  const userLevel = ROLE_HIERARCHY[userRole] || 0;
  const requiredLevel = ROLE_HIERARCHY[requiredRole] || 0;
  return userLevel >= requiredLevel;
}

/**
 * Check if user can edit submissions
 */
export function canEdit(userRole) {
  return hasRole(userRole, ROLES.AGENT);
}

/**
 * Check if user can delete submissions
 */
export function canDelete(userRole) {
  return hasRole(userRole, ROLES.ADMIN);
}

/**
 * Check if user can delete a stale Twilio verification and recreate it.
 *
 * Deleting a verification is irreversible and sends the replacement to the back
 * of Twilio's standard review queue, so it is admin-only — a strictly higher bar
 * than canSubmitToTwilio.
 */
export function canRecreateVerification(userRole) {
  return hasRole(userRole, ROLES.ADMIN);
}

/**
 * Check if user can submit to Twilio
 */
export function canSubmitToTwilio(userRole) {
  return hasRole(userRole, ROLES.AGENT);
}

/**
 * Check if user can approve/reject submissions
 */
export function canApproveReject(userRole) {
  return hasRole(userRole, ROLES.AGENT);
}

/**
 * Check if user can add notes
 */
export function canAddNotes(userRole) {
  return hasRole(userRole, ROLES.AGENT);
}

/**
 * Check if user can change status
 */
export function canChangeStatus(userRole) {
  return hasRole(userRole, ROLES.AGENT);
}

/**
 * Check if user can manage users (role management)
 */
export function canManageUsers(userRole) {
  return hasRole(userRole, ROLES.ADMIN);
}

/**
 * Check if user can upload documents
 */
export function canUploadDocuments(userRole) {
  return hasRole(userRole, ROLES.AGENT);
}

/**
 * Check if user can delete documents
 */
export function canDeleteDocuments(userRole) {
  return hasRole(userRole, ROLES.AGENT);
}

/**
 * Get role display name
 */
export function getRoleDisplayName(role) {
  const names = {
    viewer: 'Viewer',
    agent: 'Agent',
    admin: 'Administrator'
  };
  return names[role] || role;
}

/**
 * Get role badge color
 */
export function getRoleBadgeColor(role) {
  const colors = {
    viewer: 'bg-gray-100 text-gray-800',
    agent: 'bg-blue-100 text-blue-800',
    admin: 'bg-purple-100 text-purple-800'
  };
  return colors[role] || 'bg-gray-100 text-gray-800';
}
